<template>
  <div class="app-container">
    <!-- 搜索区域 -->
    <el-form :model="queryParams" ref="queryForm" size="small" :inline="true" v-show="showSearch" label-width="80px">
      <el-form-item label="学生姓名" prop="studentName">
        <el-input
          v-model="queryParams.studentName"
          placeholder="请输入学生姓名"
          clearable
          @keyup.enter.native="handleQuery"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" size="mini" @click="handleQuery">搜索</el-button>
        <el-button icon="el-icon-refresh" size="mini" @click="resetQuery">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 操作按钮区域 -->
    <el-row :gutter="10" class="mb8">
      <el-col :span="1.5">
        <el-button
          type="primary"
          plain
          icon="el-icon-plus"
          size="mini"
          @click="handleAdd"
        >新增学分记录</el-button>
      </el-col>
      <el-col :span="1.5">
        <el-button
          type="warning"
          plain
          icon="el-icon-download"
          size="mini"
          @click="handleExport"
        >导出</el-button>
      </el-col>
    </el-row>

    <!-- 数据表格 -->
    <el-table v-loading="loading" :data="logsList" @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="55" align="center" />
      <el-table-column label="编号" align="center" prop="id" />
      <el-table-column label="学生姓名" align="center" prop="studentName" />
      <el-table-column label="班级" align="center" prop="className" />
      <el-table-column label="专业" align="center" prop="majorName" />
      <el-table-column label="类型" align="center" prop="changeType">
        <template slot-scope="scope">
          <el-tag :type="scope.row.changeType === '加分' ? 'success' : 'danger'">
            {{ scope.row.changeType }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="变更分值" align="center" prop="changeValue">
        <template slot-scope="scope">
          <span :class="scope.row.changeType === '加分' ? 'positive-value' : 'negative-value'">
            {{ scope.row.changeType === '加分' ? '+' : '' }}{{ scope.row.changeValue }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="原因" align="center" prop="reason" />
      <el-table-column label="操作人" align="center" prop="operatorName" />
      <el-table-column label="操作时间" align="center" prop="operateTime" >
        <template slot-scope="scope">
          <span>{{ parseTime(scope.row.operateTime) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center" class-name="small-padding fixed-width" width="120">
        <template slot-scope="scope">
          <el-button
            size="mini"
            type="text"
            icon="el-icon-delete"
            @click="handleDelete(scope.row)"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页组件 -->
    <el-pagination background class="pagination-container" v-show="total > 0" :current-page="queryParams.pageNum"
      :page-sizes="[5, 10, 20, 50]" :page-size="queryParams.pageSize" layout="total, sizes, prev, pager, next, jumper"
      :total="total" @size-change="handleSizeChange" @current-change="handleCurrentChange" />

    <!-- 添加学分记录对话框 -->
    <el-dialog :title="title" :visible.sync="open" width="900px" append-to-body>
      <el-form ref="form" :model="form" :rules="rules" label-width="100px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="专业" prop="majorId">
              <el-select 
                v-model="form.majorId" 
                placeholder="请选择专业" 
                style="width: 100%"
                @change="handleMajorChange"
                clearable
              >
                <el-option 
                  v-for="major in majorOptions" 
                  :key="major.majorId" 
                  :label="major.majorName" 
                  :value="major.majorId"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="班级" prop="classId">
              <el-select 
                v-model="form.classId" 
                placeholder="请先选择班级" 
                style="width: 100%"
                @change="handleClassChange"
                :disabled="!form.majorId"
                clearable
              >
                <el-option 
                  v-for="classItem in classOptions" 
                  :key="classItem.id" 
                  :label="classItem.className" 
                  :value="classItem.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="学生" prop="studentId">
              <el-select 
                v-model="form.studentId" 
                placeholder="请先选择学生" 
                style="width: 100%"
                :disabled="!form.classId"
                clearable
              >
                <el-option 
                  v-for="student in studentOptions" 
                  :key="student.id" 
                  :label="student.name" 
                  :value="student.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="学分规则" prop="ruleCode">
              <el-select 
                v-model="form.ruleCode" 
                placeholder="请选择学分规则" 
                style="width: 100%"
                @change="handleRuleChange"
                clearable
              >
                <el-option 
                  v-for="rule in ruleOptions" 
                  :key="rule.ruleCode" 
                  :label="rule.ruleName" 
                  :value="rule.ruleCode"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="变更分值" prop="changeValue">
          <el-input-number 
            v-model="form.changeValue" 
            placeholder="请输入变更分值" 
            :min="-100" 
            :max="100" 
            style="width: 100%"
          />
        </el-form-item>
        
        <el-form-item label="原因" prop="reason">
          <el-input 
            v-model="form.reason" 
            type="textarea" 
            :rows="3" 
            placeholder="请输入原因" 
          />
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="submitForm">确 定</el-button>
        <el-button @click="cancel">取 消</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listLogs, addLogs, delLogs } from "@/api/system/logs";
import { listMajors } from "@/api/OmdMajors/majors";
import { listClasses } from "@/api/OmdClasses/classes";
import { listStudents } from "@/api/OmdStudents/students";
import { listRules } from "@/api/system/rules";
import { download } from "@/utils/request";

export default {
  name: "CreditLog",
  data() {
    return {
      // 遮罩层
      loading: true,
      // 选中数组
      ids: [],
      // 显示搜索条件
      showSearch: true,
      // 总条数
      total: 0,
      // 学分操作记录表格数据
      logsList: [],
      // 弹出层标题
      title: "",
      // 是否显示弹出层
      open: false,
      // 专业选项
      majorOptions: [],
      // 班级选项
      classOptions: [],
      // 学生选项
      studentOptions: [],
      // 规则选项
      ruleOptions: [],
      // 查询参数
      queryParams: {
        pageNum: 1,
        pageSize: 100,
        studentName: null,
      },
      // 表单参数
      form: {
        majorId: null,
        classId: null,
        studentId: null,
        ruleCode: null,
        changeValue: 0,
        reason: null,
        changeType: '奖励'
      },
      // 表单校验
      rules: {
        majorId: [
          { required: true, message: "请选择专业", trigger: "change" }
        ],
        classId: [
          { required: true, message: "请选择班级", trigger: "change" }
        ],
        studentId: [
          { required: true, message: "请选择学生", trigger: "change" }
        ],
        ruleCode: [
          { required: true, message: "请选择学分规则", trigger: "change" }
        ],
        changeValue: [
          { required: true, message: "分值不能为空", trigger: "blur" }
        ],
        reason: [
          { required: true, message: "原因不能为空", trigger: "blur" }
        ]
      }
    };
  },
  created() {
    this.getList();
    this.getMajorOptions();
    this.getRuleOptions();
  },
   computed: {
    identity() {
      return this.$store.state.identity
    },
  },
  methods: {
    /** 查询学分操作记录列表 */
    getList() {
      this.loading = true;
      listLogs(this.queryParams).then(response => {
        this.logsList = response.rows;
        this.total = response.total;
        this.loading = false;
      });
    },
    
    /** 获取专业选项 */
    getMajorOptions() {
      listMajors().then(response => {
        this.majorOptions = response.rows || [];
        console.log("专业",response);
      });
    },
    
    /** 获取规则选项 */
    getRuleOptions() {
      listRules({ pageNum: 1, pageSize: 1000 }).then(response => {
        this.ruleOptions = response.rows || [];
        console.log("规则",response);
      });
    },
    
    /** 专业变更处理 */
    handleMajorChange(majorId) {
      // 重置班级和学生选择
      this.form.classId = null;
      this.form.studentId = null;
      this.classOptions = [];
      this.studentOptions = [];
      
      if (majorId) {
        // 根据专业ID获取班级列表
        listClasses({ majorId: majorId, pageNum: 1, pageSize: 1000 }).then(response => {
          this.classOptions = response.rows || [];
          console.log("班级",this.classOptions);
        }).catch(error => {
          console.error("获取班级列表失败:", error);
          this.$message.error("获取班级列表失败");
        });
      }
    },
    
    /** 班级变更处理 */
    handleClassChange(classId) {
      // 重置学生选择
      this.form.studentId = null;
      this.studentOptions = [];
      
      if (classId) {
        // 根据班级ID获取学生列表
        listStudents({ classId: classId, pageNum: 1, pageSize: 1000 }).then(response => {
          this.studentOptions = response.rows || [];
          console.log("学生",response.rows);
        }).catch(error => {
          console.error("获取学生列表失败:", error);
          this.$message.error("获取学生列表失败");
        });
      }
    },
    
    /** 规则变更处理 */
    handleRuleChange(ruleCode) {
      if (ruleCode) {
        const selectedRule = this.ruleOptions.find(rule => rule.ruleCode === ruleCode);
        if (selectedRule) {
          this.form.changeValue = selectedRule.changeValue;
          this.form.reason = selectedRule.ruleName;
          this.form.changeType = selectedRule.ruleType === '加分' ? '加分' : '扣分';
        }
      }
    },
    
    /**
     * 每页条数变化事件
     */
    handleSizeChange(val) {
      this.queryParams.pageSize = val;
      this.getList();
    },

    /**
     * 当前页变化事件
     */
    handleCurrentChange(val) {
      this.queryParams.pageNum = val;
      this.getList();
    },
    
    // 取消按钮
    cancel() {
      this.open = false;
      this.reset();
    },
    
    // 表单重置
    reset() {
      this.form = {
        majorId: null,
        classId: null,
        studentId: null,
        ruleCode: null,
        changeValue: 0,
        reason: null,
        changeType: '加分',
        operatorId: sessionStorage.getItem("user")
      };
      this.classOptions = [];
      this.studentOptions = [];
      if (this.$refs.form) {
        this.$refs.form.resetFields();
      }
    },
    
    /** 搜索按钮操作 */
    handleQuery() {
      this.queryParams.pageNum = 1;
      this.getList();
    },
    
    /** 重置按钮操作 */
    resetQuery() {
      if (this.$refs.queryForm) {
        this.$refs.queryForm.resetFields();
      }
      this.handleQuery();
    },
    
    // 多选框选中数据
    handleSelectionChange(selection) {
      this.ids = selection.map(item => item.id);
    },
    
    /** 新增按钮操作 */
    handleAdd() {
      this.reset();
      this.open = true;
      this.title = "添加学分记录";
    },
    
    /** 提交按钮 */
    submitForm() {
      this.$refs["form"].validate(valid => {
        if (valid) {
          addLogs(this.form).then(response => {
            this.$modal.msgSuccess("新增成功");
            this.open = false;
            this.getList();
            console.log(response);
          }).catch(error => {
            console.error("新增失败:", error);
            this.$modal.msgError("新增失败");
          });
        }
      });
    },
    
    /** 删除按钮操作 */
    handleDelete(row) {
      const ids = row.id || this.ids;
      this.$modal.confirm('是否确认删除该学分记录？').then(function() {
        return delLogs(ids);
      }).then(() => {
        this.getList();
        this.$modal.msgSuccess("删除成功");
      }).catch(() => {});
    },
    
    /** 导出按钮操作 */
    handleExport() {
      download('system/logs/export', {
        ...this.queryParams
      }, `logs_${new Date().getTime()}.xlsx`)
    },
    
    // 简化版：只处理年月日的格式化
    parseTime(isoString) {
      if (!isoString) return '';
      
      try {
        const date = new Date(isoString);
        if (isNaN(date.getTime())) {
          return '无效日期';
        }
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
      } catch (error) {
        return '解析失败';
      }
    }
  }
};
</script>

<style scoped>
/* 主容器样式 */
.app-container {
  padding: 20px;
}

/* 表格操作按钮紧凑样式 */
.small-padding .el-button {
  padding: 5px;
  margin-left: 0;
}
/* 加分值样式 - 绿色 */
.positive-value {
  color: #67c23a;
  font-weight: bold;
}

/* 扣分值样式 - 红色 */
.negative-value {
  color: #f56c6c;
  font-weight: bold;
}
/* 表格头部*/
::v-deep .el-table__header-wrapper .el-table__header th {
  background-color: #f4f4f4 !important;
  color: #000 !important;
}

/* 分页器样式 */
::v-deep .el-pager li.active {
  background-color: var(--primary-color) !important;
  color: white !important;
  border-color: var(--primary-color) !important;
  font-weight: bold;
  cursor: pointer;
}

/* 分页器样式 */
::v-deep .el-pager li.active:hover {
  background-color: #d6336c !important;
  color: white !important;
  font-weight: bold;
  cursor: pointer;
}

/*按钮的样式 主要按钮 的样式*/
::v-deep .el-button--primary {
  color: #fff;
  background-color: var(--primary-color);
  border: 1px solid var(--primary-color);
}

/*鼠标获得焦点的样式*/
::v-deep .el-button--primary:hover {
  background-color: #d6336c !important;
}

/* 文本框鼠标获得焦点边框 */
::v-deep .el-input__inner:focus {
  border-color: var(--primary-color) !important;
}

/* 如果是 textarea */
::v-deep .el-textarea__inner:focus {
  border-color: var(--primary-color) !important;
}

/* 在全局样式或组件样式中添加 */
::v-deep .confirm-delete-button {
  background-color: #f56c6c !important;
  border-color: #f56c6c !important;
}

::v-deep .confirm-delete-button:hover {
  background-color: #e05c5c !important;
  border-color: #e05c5c !important;
}

/* 表单布局优化 */
.mb8 {
  margin-bottom: 15px;
}

.pagination-container {
  margin-top: 20px;
}
</style>